import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEnvelope, faLock } from '@fortawesome/free-solid-svg-icons';
import { faGoogle as faGoogleBrand, faGithub as faGithubBrand } from '@fortawesome/free-brands-svg-icons';
import { useAuth } from '../context/AuthContext';
import api from '../config/api';
import AuthLayout from '../components/AuthLayout';
import SEO from '../components/SEO';

const Login: React.FC = () => {
  const { t } = useTranslation();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [socialLoginEnabled, setSocialLoginEnabled] = useState(true);
  const { login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const getApiBaseUrl = () => {
    const envUrl = process.env.REACT_APP_API_URL;
    if (envUrl) return envUrl.endsWith('/api') ? envUrl : `${envUrl}/api`;
    return 'http://localhost:5000/api';
  };
  const API_BASE_URL = getApiBaseUrl();
  const examId = (location.state as { examId?: string })?.examId || new URLSearchParams(location.search).get('examId');

  useEffect(() => {
    const fetchSettings = async () => {
      try {
        const response = await api.get('/settings');
        const socialLoginSetting = response.data.settings?.social_login_enabled;
        if (socialLoginSetting !== undefined) setSocialLoginEnabled(socialLoginSetting.value);
      } catch {
        /* default enabled */
      }
    };
    fetchSettings();
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      await login(email, password);
      navigate(examId ? `/subscription/${examId}` : '/dashboard');
    } catch (err: unknown) {
      const axiosErr = err as { response?: { data?: { error?: string } } };
      setError(axiosErr.response?.data?.error || t('auth.loginFailed'));
    } finally {
      setLoading(false);
    }
  };

  const handleSocial = (provider: 'google' | 'github') => {
    if (examId) localStorage.setItem('pendingExamId', examId);
    window.location.href = `${API_BASE_URL}/auth/${provider}`;
  };

  return (
    <>
      <SEO title={t('auth.seo.loginTitle')} description={t('auth.seo.loginDescription')} noindex />
      <AuthLayout title={t('auth.welcomeBack')} subtitle={t('auth.loginSubtitle', 'Sign in to continue your prep')}>
        {error && <div className="gp-alert-error" role="alert">{error}</div>}

        <form onSubmit={handleSubmit} className="space-y-5">
          <div>
            <label htmlFor="email" className="block text-sm font-medium mb-2" style={{ color: 'var(--gp-ink)' }}>
              {t('auth.email')}
            </label>
            <div className="relative">
              <FontAwesomeIcon icon={faEnvelope} className="absolute left-3 top-1/2 -translate-y-1/2 text-sm gp-text-muted" aria-hidden="true" />
              <input type="email" id="email" value={email} onChange={(e) => setEmail(e.target.value)} required className="input-field pl-10" autoComplete="email" />
            </div>
          </div>

          <div>
            <div className="flex items-center justify-between mb-2">
              <label htmlFor="password" className="block text-sm font-medium" style={{ color: 'var(--gp-ink)' }}>
                {t('auth.password')}
              </label>
              <Link to="/forgot-password" className="gp-link text-sm">
                {t('auth.forgotPassword')}
              </Link>
            </div>
            <div className="relative">
              <FontAwesomeIcon icon={faLock} className="absolute left-3 top-1/2 -translate-y-1/2 text-sm gp-text-muted" aria-hidden="true" />
              <input type="password" id="password" value={password} onChange={(e) => setPassword(e.target.value)} required className="input-field pl-10" autoComplete="current-password" />
            </div>
          </div>

          <button type="submit" className="w-full gp-btn-primary py-3" disabled={loading}>
            {loading ? t('auth.loggingIn') : t('auth.login')}
          </button>
        </form>

        {socialLoginEnabled && (
          <div className="mt-6">
            <div className="relative my-6">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t" style={{ borderColor: 'var(--gp-border)' }} />
              </div>
              <div className="relative flex justify-center text-sm">
                <span className="px-3 gp-text-muted" style={{ background: 'var(--gp-surface)' }}>{t('auth.or')}</span>
              </div>
            </div>
            <div className="space-y-3">
              <button type="button" onClick={() => handleSocial('google')} className="gp-social-btn">
                <FontAwesomeIcon icon={faGoogleBrand} className="text-lg text-red-500" aria-hidden="true" />
                {t('auth.loginWithGoogle')}
              </button>
              <button type="button" onClick={() => handleSocial('github')} className="gp-social-btn">
                <FontAwesomeIcon icon={faGithubBrand} className="text-lg" aria-hidden="true" />
                {t('auth.loginWithGitHub')}
              </button>
            </div>
          </div>
        )}

        <p className="mt-6 text-center text-sm gp-text-muted">
          {t('auth.dontHaveAccount')}{' '}
          <Link to="/register" state={examId ? { examId } : undefined} className="gp-link">
            {t('auth.signUp')}
          </Link>
        </p>
      </AuthLayout>
    </>
  );
};

export default Login;
